/**
 * AI Diagnostics — logs all AI requests/responses for developer debugging.
 *
 * Stored in localStorage, viewable in Settings > Developer > AI Diagnostics.
 * Keeps the last 50 entries, each with request summary, response status, timing.
 */

const AI_DIAG_KEY = "totodo_ai_diagnostics"
const MAX_ENTRIES = 50

export interface AiDiagEntry {
  id: string
  timestamp: string
  endpoint: string
  method: string
  /** HTTP status code (0 if network error) */
  status: number
  /** Duration in ms */
  durationMs: number
  /** First 200 chars of error message if failed */
  error?: string
  /** Whether it was a streaming response */
  streaming: boolean
  /** Request body summary (first 200 chars) */
  requestSummary?: string
  /** Response summary (first 200 chars of content or error) */
  responseSummary?: string
}

export function logAiRequest(entry: Omit<AiDiagEntry, "id" | "timestamp">): void {
  try {
    const entries = getAiDiagEntries()
    entries.push({
      ...entry,
      id: crypto.randomUUID(),
      timestamp: new Date().toISOString(),
    })
    // Keep only last MAX_ENTRIES
    if (entries.length > MAX_ENTRIES) entries.splice(0, entries.length - MAX_ENTRIES)
    localStorage.setItem(AI_DIAG_KEY, JSON.stringify(entries))
  } catch {
    // Best effort — localStorage might be full
  }
}

export function getAiDiagEntries(): AiDiagEntry[] {
  try {
    const raw = localStorage.getItem(AI_DIAG_KEY)
    return raw ? JSON.parse(raw) : []
  } catch {
    return []
  }
}

export function clearAiDiagEntries(): void {
  localStorage.removeItem(AI_DIAG_KEY)
}
